import { useParams } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

import axios from "../../lib/axios";
import GeneHuntViewer from "../../components/view/GeneHuntViewer";

const TaskView = () => {
  const params = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState({
    progress: 0,
    progress_message: "",
    status: "",
  });
  const ws = useRef(null);

  // WebSocket connection
  useEffect(() => {
    // Only connect if task is not completed and task_id is available
    if (task?.status?.toLowerCase() === "pending") {
      const wsUrl = `wss://1r9yo2hhm1.execute-api.us-west-2.amazonaws.com/production?id=${params.task_id}&type=task`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log("WebSocket Connected");
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WebSocket message received:", data);
          setProgressData({
            progress: data.progress || 0,
            progress_message: data.progress_message || "",
            status: data.status || "",
          });
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.current.onclose = () => {
        console.log("WebSocket Disconnected");
      };
    }

    // Clean up WebSocket connection on component unmount
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [params.task_id, task?.status]);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const response = await axios.get(`/v2/tasks/${params.task_id}`);
        setTask(response.data.data);
      } catch (err) {
        setError("Task not found");
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [params.task_id]);

  if (loading) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-gray-500">Loading task details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-gray-500">Task not found</div>
      </div>
    );
  }

  return (
    <section className="w-full p-16">
      <div className="w-full flex flex-col items-center justify-center gap-7">
        {/* header */}
        <div className="w-full rounded shadow-sm p-4 border bg-white flex justify-center items-center">
          <h1 className="font-bold text-gray-500 text-center">Task Details</h1>
        </div>
        <div className="w-full rounded shadow-sm p-4 border bg-white flex justify-between items-center">
          <div>
            <h1 className="font-bold text-gray-500">
              <span className="text-blue-500">
                {" "}
                {task.task_id}{" "}
                <span className="text-xs text-gray-500 ml-2">
                  ({progressData.status.toLowerCase() || task.status.toLowerCase()})
                </span>
              </span>
            </h1>
            <p>
              {task.file?.split("/").pop() || "File not available"}
              {task.file_size && (
                <span className="text-gray-500 text-sm ml-2">
                  ({parseFloat(task.file_size).toFixed(2)} MB)
                </span>
              )}
            </p>
            {task.status.toLowerCase() !== "completed" && (
              <div className="mt-2 flex items-center gap-3">
                {task.status.toLowerCase() !== "completed" && (
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                )}
                <div className="flex-1">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progressData.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Progress: {progressData.progress}% - {progressData.progress_message}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3 flex flex-col items-center">
            {(task.status?.toLowerCase() === "completed" || progressData.status?.toLowerCase() === "completed") ? (
              <a
                href={`https://storage.kinggenomics.com/output/task/${task.task_id}/result.zip`}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Download Results
              </a>
            ) : (
              <button
                className="bg-gray-300 text-gray-600 px-4 py-2 rounded cursor-not-allowed"
                disabled
              >
                Results not available
              </button>
            )}
            <div className=" text-gray-400 text-xs flex flex-col">
             <p><span>Create Time:</span> {new Date(task.created_at).toLocaleString()}</p>
             {task.started_at && <p><span>Start Time:</span> {new Date(task.started_at).toLocaleString()}</p>}
             {task.finished_at && <p><span>End Time:</span> {new Date(task.finished_at).toLocaleString()}</p>}
            </div>
          </div>
        </div>
        {(task.status?.toLowerCase() === "completed" || progressData.status?.toLowerCase() === "completed") && task.script === "gene_hunt" && (
          <GeneHuntViewer type="task" id={task.task_id} />
        )}
      </div>
    </section>
  );
};

export default TaskView;
